import React, { useEffect, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedSection from '../common/AnimatedSection';

const GalleryPreview = ({ data = [], loadMore, loading, hasMore }) => {
  const observer_target = useRef(null);
  const [selected_image, set_selected_image] = useState(null);

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    if (loadMore) loadMore();
  }, [loading, hasMore, loadMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (observer_target.current) observer.observe(observer_target.current);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  return (
    <section id="gallery" className="py-24 bg-white overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <AnimatedSection>
            <div className="mb-4">
              <span className="inline-block px-4 py-1.5 bg-primary/[0.06] text-primary text-xs font-bold rounded-full tracking-widest uppercase">
                our milestones
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-dark leading-tight tracking-tight max-w-2xl">
              Capturing the <span className="text-primary italic">impact</span> of professional excellence
            </h2>
          </AnimatedSection>
        </div>

        {/* grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {data?.map((batch) => 
              batch?.images?.map((img, i) => (
                <motion.div
                  key={img?.id || `${batch.id}-${i}`}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -8 }}
                  onClick={() => set_selected_image(img)}
                  className="group relative aspect-square rounded-3xl overflow-hidden bg-dark/[0.03] border border-dark/[0.04] cursor-zoom-in"
                >
                  <img
                    src={img?.image}
                    alt={img?.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-6 flex flex-col justify-end">
                    <span className="text-primary text-[10px] font-bold uppercase tracking-widest mb-1">
                      {img?.category}
                    </span>
                    <h4 className="text-white font-bold text-sm uppercase">
                      {img?.title}
                    </h4>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>

        {/* status / observer */}
        <div ref={observer_target} className="w-full py-16 flex flex-col items-center justify-center">
          {loading && (
            <div className="flex gap-2">
              {[0, 1, 2].map((dot) => (
                <motion.div
                  key={dot}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ repeat: Infinity, duration: 1, delay: dot * 0.2 }}
                  className="w-2.5 h-2.5 rounded-full bg-primary"
                />
              ))}
            </div>
          )}
          {!hasMore && data?.length > 0 && (
            <p className="text-dark/30 text-[10px] font-black uppercase tracking-[0.3em]">
              showing all gallery milestones
            </p>
          )}
        </div>
      </div>

      {/* ─── clean pop-out (no card, no button) ─── */}
      <AnimatePresence>
        {selected_image && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-12">
            
            {/* background blur overlay (click to close) */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => set_selected_image(null)}
              className="absolute inset-0 bg-dark/80 backdrop-blur-xl cursor-zoom-out"
            />

            {/* floating image (no background card) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="relative z-10 w-full h-full flex flex-col items-center justify-center pointer-events-none"
            >
              <img 
                src={selected_image.image} 
                alt={selected_image.title} 
                className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl pointer-events-auto"
                onClick={(e) => e.stopPropagation()} // prevents closing when clicking the image itself
              />
              
              {/* minimalistic caption */}
              <div className="mt-6 text-center pointer-events-auto">
                <p className="text-primary text-[10px] font-black uppercase tracking-[0.4em] mb-1">
                  {selected_image.category}
                </p>
                <h4 className="text-white text-2xl font-bold uppercase tracking-tight">
                  {selected_image.title}
                </h4>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GalleryPreview;